import { mergeAttributes, Node, nodePasteRule } from "@tiptap/core";
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  NodeViewProps,
} from "@tiptap/react";
import { Box, IconButton } from "@mui/material";
import { ExternalLink, X } from "lucide-react";
import { TextSelection } from "@tiptap/pm/state";
import { getEditorWidth } from "./utils";

// Regex to match Canva design URLs and extract the design ID
const CANVA_REGEX =
  /^(?:https?:\/\/)?(?:www\.)?canva\.com\/design\/(([a-zA-Z0-9_-]+)(\/([a-zA-Z0-9_-]+))*)\/(edit|view)(?:\?.*)?/;
const CANVA_REGEX_GLOBAL =
  /^(?:https?:\/\/)?(?:www\.)?canva\.com\/design\/(([a-zA-Z0-9_-]+)(\/([a-zA-Z0-9_-]+))*)\/(edit|view)(?:\?.*)?$/g;

const isValidCanvaUrl = (url: string) => {
  return url.match(CANVA_REGEX);
};

const styleObjectToCssString = (styles: Record<string, any>): string => {
  return Object.entries(styles)
    .map(([key, value]) => {
      const kebabKey: string = key
        .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
        .toLowerCase();
      return `${kebabKey}: ${value};`;
    })
    .join(" ");
};

// Helper function to handle width/height values with or without units
const formatDimension = (value: string | number): string => {
  if (typeof value === "number") {
    return `${value}px`;
  }

  // If it's already a string with px or % unit, return as is
  if (
    typeof value === "string" &&
    (value.endsWith("px") || value.endsWith("%"))
  ) {
    return value;
  }

  // Otherwise, assume it's a number without units and add px
  return `${value}px`;
};

const CanvaComponent: React.FC<NodeViewProps> = ({ node, view, getPos }) => {
  // We want 100% width, but don't have control over the parent to also set width,
  // We get the tiptap container width before we render the card.
  const editorWidth = getEditorWidth(view);

  const handleClose = () => {
    // Create a link mark with the URL
    const linkMark = view.state.schema.marks.link.create({
      href: node.attrs.sourceLink,
    });

    // Replace the node with linked text and set cursor at the end
    const transaction = view.state.tr.replaceWith(
      getPos(),
      getPos() + node.nodeSize,
      view.state.schema.text(node.attrs.sourceLink, [linkMark]),
    );

    // Calculate the correct end position after the replacement
    const endPos = getPos() + node.attrs.sourceLink.length;
    const resolvedPos = transaction.doc.resolve(endPos);

    // Set cursor at the end of the inserted text
    transaction.setSelection(TextSelection.near(resolvedPos));

    view.dispatch(transaction);
  };

  // Reset the lastIndex to ensure the regex works correctly when called multiple times
  CANVA_REGEX.lastIndex = 0;
  // Use the exec method to search for a match
  const match = CANVA_REGEX.exec(node.attrs.sourceLink);
  // match[1] will be the captured group if a match is found
  const designId = match ? match[1] : "";
  // Build the embed URL
  const embedUrl = `https://www.canva.com/design/${designId}/view?embed`;

  return (
    <NodeViewWrapper className="canva-embed-component">
      <Box
        sx={{
          position: "relative",
          width: formatDimension(node.attrs.width),
          height: formatDimension(node.attrs.height),
          maxWidth: `${editorWidth ? editorWidth * 0.8 : 350}px`,
          paddingBottom: "20px",
          margin: "12px 0",
        }}
      >
        <iframe
          src={embedUrl}
          loading="lazy"
          style={{
            position: "absolute",
            width: `100%`,
            height: `100%`,
            maxHeight: "100%",
            maxWidth: "100%",
            top: 0,
            left: 0,
            padding: 0,
            margin: 0,
            border: "none",
          }}
          allowFullScreen
        />
        <Box
          sx={{
            position: "absolute",
            top: "16px",
            right: "16px",
            display: "flex",
            gap: "8px",
            zIndex: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              window.open(node.attrs.sourceLink, "_blank");
            }}
            sx={{
              padding: "5px",
              "&:hover": {
                backgroundColor: "#e0e0e0",
                borderRadius: "6px",
              },
            }}
          >
            <ExternalLink size={16} />
          </IconButton>
          {view.editable && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              sx={{
                padding: "5px",
                "&:hover": {
                  backgroundColor: "#e0e0e0",
                  borderRadius: "6px",
                },
              }}
            >
              <X size={16} />
            </IconButton>
          )}
        </Box>
      </Box>
    </NodeViewWrapper>
  );
};

export interface CanvaOptions {
  addPasteHandler: boolean;
  inline: boolean;
  width: number;
  height: number;
  HTMLAttributes: Record<string, any>;
  allowFullscreen: boolean;
  lazy: boolean;
  noBorder: boolean;
}

type SetCanvaEmbedOptions = {
  sourceLink: string;
  width?: number;
  height?: number;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    canva: {
      /**
       * Insert a Canva design
       */
      setCanvaEmbed: (options: SetCanvaEmbedOptions) => ReturnType;
    };
  }
}

export default Node.create<CanvaOptions>({
  name: "canva",

  addOptions() {
    return {
      addPasteHandler: true,
      inline: false,
      HTMLAttributes: {},
      height: 600,
      width: 720,
      allowFullscreen: true,
      lazy: true,
      noBorder: true,
    };
  },

  group() {
    return this.options.inline ? "inline" : "block";
  },

  inline: false,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      sourceLink: {
        default: null,
      },
      width: {
        default: this.options.width,
      },
      height: {
        default: this.options.height,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-canva-embed]",
        priority: 63,
        getAttrs: (element) => {
          const iframe = element.querySelector("iframe");
          if (!iframe) return false;

          const sourceLink = iframe.getAttribute("src");
          const width = iframe.style.width || this.options.width;
          const height = iframe.style.height || this.options.height;

          return {
            sourceLink,
            width,
            height,
          };
        },
      },
    ];
  },

  addCommands() {
    return {
      setCanvaEmbed:
        (options: SetCanvaEmbedOptions) =>
        ({ commands }) => {
          if (!isValidCanvaUrl(options.sourceLink)) {
            return false;
          }

          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },

  addPasteRules() {
    if (!this.options.addPasteHandler) {
      return [];
    }

    return [
      nodePasteRule({
        find: CANVA_REGEX_GLOBAL,
        type: this.type,
        getAttributes: (match) => {
          return { sourceLink: match[0] };
        },
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(CanvaComponent);
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = HTMLAttributes as SetCanvaEmbedOptions;
    // Reset the lastIndex to ensure the regex works correctly when called multiple times
    CANVA_REGEX.lastIndex = 0;
    // Use the exec method to search for a match
    const match = CANVA_REGEX.exec(attrs.sourceLink);
    // match[1] will be the captured group if a match is found
    const designId = match ? match[1] : "";
    // Build the embed URL
    const embedUrl = `https://www.canva.com/design/${designId}/view?embed`;
    return [
      "div",
      {
        "data-canva-embed": "",
        style: styleObjectToCssString({
          position: "relative",
          width: formatDimension(this.options.width),
          height: formatDimension(this.options.height),
          maxWidth: "90%", // Max out at 90% of the screen width
          paddingBottom: "56.25%", // Maintains a 16:9 aspect ratio
        }),
      },
      [
        "iframe",
        mergeAttributes(this.options.HTMLAttributes, {
          src: embedUrl,
          loading: this.options.lazy ? "lazy" : undefined,
          style: styleObjectToCssString({
            position: "absolute",
            width: formatDimension(this.options.width),
            height: formatDimension(this.options.height),
            maxHeight: "100%",
            maxWidth: "100%",
            top: 0,
            left: 0,
            padding: 0,
            margin: 0,
            ...(this.options.noBorder ? { border: "none" } : {}),
          }),
          ...(this.options.allowFullscreen
            ? {
                allowfullscreen: "allowfullscreen",
                allow: "fullscreen",
              }
            : {}),
        }),
      ],
    ];
  },
});
